import {
  ChangeDetectorRef, Component, Input, OnChanges, OnInit, SimpleChanges
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { filter, finalize, tap, map, startWith, switchMap, takeUntil } from 'rxjs/operators';

import { WithChangesSubject } from 'core/mixins/with-changes-subject';
import { WithDisposableSubject } from 'core/mixins/with-disposable-subject';
import { ToastrService } from 'core/toastr.service';
import {
  AddOrUpdateEmailDialog
} from 'entity/dialogs/add-or-update-email/add-or-update-email.dialog';
import {
  SharedContactInfoDialog
} from 'entity/dialogs/shared-contact-info/shared-contact-info.dialog';
import { getSafe, sortByBest } from 'helpers';

import {
  EmailDetails, EntityWithEmails, FieldStatus, FieldStatusInput, GetEntityById2GQL,
  UpdateEmailGQL, UpdateEmailInput
} from 'generated/graphql';
import { EntityDuplicateEntry, EntityDuplicateFinderService } from 'entity/helpers/entity-duplicate-finder.service';
import { ActivatedRoute, Router } from '@angular/router';

@Component({
  selector: 'app-entity-emails-table',
  templateUrl: './entity-emails-table.component.html'
})
export class EntityEmailsTableComponent
  extends WithDisposableSubject(WithChangesSubject(class { }))
  implements OnChanges, OnInit {
  isSaving = false;

  @Input() entityId: string;
  @Input() campaignId: string;
  @Input() showTaskDoneMark: boolean;
  @Input() readonly: boolean;

  entity: EntityWithEmails.Fragment = null;
  modalCheckedOnce = false;
  showBad = false;

  private dupesEntry: EntityDuplicateEntry;

  sortByBest = sortByBest;

  constructor(
    private _getEntityGQL: GetEntityById2GQL,
    private _updateEmailGQL: UpdateEmailGQL,
    private _duplicateFinder: EntityDuplicateFinderService,
    private _cd: ChangeDetectorRef,
    private _dialog: MatDialog,
    private _toastrService: ToastrService,
    private _router: Router,
    private _activatedRoute: ActivatedRoute
  ) {

    super();
  }

  ngOnInit() {
    this.watchForChangeKeys('entityId', 'campaignId').pipe(
      startWith<any, any>(null),
      takeUntil(this.disposer),
      map(() => ({ campaignId: this.campaignId, entityId: this.entityId })),
      filter(({ campaignId, entityId }) => !!campaignId || !!entityId),
      switchMap(({ campaignId, entityId }) => this._getEntityGQL.watch({
        entityId,
        campaignId,
        withToplines: true,
        withBestEmail: true,
        withEmails: true
      }, {
        fetchPolicy: 'cache-first'
      }).valueChanges.pipe(takeUntil(this.disposer))),
      map(({ data }) => data?.getEntity),
      tap((entity) => this.entity = entity),
      switchMap(async entity => {
        if (!this.readonly) {
          const dupesEntry = await this._duplicateFinder.initEntityDuplicateEntry(entity.id, entity.campaignContextId);
          return { entity, dupesEntry };
        };

        return { entity, dupesEntry: null };
      }),
      takeUntil(this.disposer)
    ).subscribe(({ entity, dupesEntry }) => {
      this.entity = entity as any;
      this.dupesEntry = dupesEntry;
      this._cd.markForCheck();
    });
  }

  isSharedEmail(emailAddress: string) {
    return this.dupesEntry?.emailMatches(emailAddress).length > 0;
  }

  ngOnChanges(changes: SimpleChanges) {
    super.ngOnChanges(changes);
  }

  onAddNew() {
    AddOrUpdateEmailDialog.open(this._dialog, {
      data: {
        entityId: this.entity.id,
        campaignId: this.entity.campaignContextId,
        email: null
      }
    });
  }

  onEdit(email: EmailDetails.Fragment) {
    AddOrUpdateEmailDialog.open(this._dialog, {
      data: {
        entityId: this.entity.id,
        campaignId: this.entity.campaignContextId,
        email
      }
    });
  }

  onSetStatus(email: EmailDetails.Fragment, status: FieldStatusInput) {
    if (this.isSaving) {
      return;
    }
    this.isSaving = true;

    if ([FieldStatusInput.Bad, FieldStatusInput.DoNotContact].includes(status)) {
      const shouldMarkBad = confirm(
        `Are you sure you would like to mark this e-mail address as ${status.split('_').join(' ')}?`
      );
      if (!shouldMarkBad) {
        this.isSaving = false;
        return;
      }
    }
    const input: UpdateEmailInput = {
      emailId: email.id,
      campaignId: this.campaignId,
      entityId: this.entity.id,
      changes: {
        status,
        email: email.email,
        emailType: email.emailType
      }
    };

    this._updateEmailGQL.mutate({ input }).pipe(
      finalize(() => this.isSaving = false),
    ).subscribe(
      (_) => this._toastrService.success('Changes saved'),
    );
  }

  onSetSubscribed(email: EmailDetails.Fragment, subscribed: boolean) {
    if (this.isSaving) {
      return;
    }
    this.isSaving = true;

    const input: UpdateEmailInput = {
      emailId: email.id,
      campaignId: this.campaignId,
      entityId: this.entity.id,
      changes: {
        subscribe: subscribed
      }
    };

    this._updateEmailGQL.mutate({ input }).pipe(
      finalize(() => this.isSaving = false),
    ).subscribe(
      (_) => this._toastrService.success('Changes saved'),
    );
  }

  isVerified(email: EmailDetails.Fragment) {
    return email.status === FieldStatus.Verified;
  }

  isBad(item: EmailDetails.Fragment) {
    return getSafe(() => item.status === 'bad', true);
  }

  isDoNotContact(item: EmailDetails.Fragment) {
    return item.status === FieldStatus.DoNotContact;
  }

  getBadCount(items: EmailDetails.Fragment[]) {
    return getSafe(() => items.filter(i => this.isBad(i)).length, 0);
  }

  onFlagSharedInfoClick(email: EmailDetails.Fragment) {
    // this.setQueryParamsModalState(email.id);
    const entityMatches = this.dupesEntry?.emailMatches(email.email);
    SharedContactInfoDialog.open(this._dialog, {
      data: {
        entityId: this.entity.id,
        campaignId: this.entity.campaignContextId,
        email,
        entityMatches
      }
    });
  }

  setQueryParamsModalState(id: string) {
    this._router.navigate([], {
      relativeTo: this._activatedRoute,
      queryParams: { potentialDupesFor: `email-${id}` },
      queryParamsHandling: 'merge'
    });
  }
}
